import React, { useMemo } from 'react';
import {
  Card, CardActions, CardContent, CardHeader, Divider, List,
} from '@mui/material';
import { priceToString } from 'shared/utils';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { TextAreaEditor } from 'shared/components/data-display/editors';
import CouponApplicableBy from './CouponApplicableBy';
import {
  discountDurationEnums,
  discountTypeEnums,
  numberOfRedemptionEnums,
  redemptionCodeEnums,
} from './helper';

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {Object<string, *>} props.coupon
 * @param {string} props.couponId
 * @param {Array<Object>} props.couponRules
 * @param {string} props.ruleType - One of: 'tenant', 'property', 'complex', 'manager', 'global'
 * @param {Function} [props.saveChanges]
 * @param {number} [props.tenantId=0]
 * @param {string} [props.tenantName='']
 * @param {Function} [props.updateValue]
 * @param {Object<string, *>} [props.updatedData]
 * @param {boolean} [props.couponIsCreated=false]
 */
export default function CouponInfo(props) {
  const {
    children,
    coupon,
    couponId,
    couponRules,
    ruleType,
    saveChanges = () => null,
    tenantId = 0,
    tenantName = '',
    updateValue = () => null,
    updatedData = { editEnabled: false },
    couponIsCreated = false,
  } = props;

  const {
    amountOff,
    description,
    discountType,
    duration,
    durationInMonths,
    idType,
    maxRedemption,
    percentOff,
    redeemBy,
    redemptionType,
    timesRedeemed,
  } = coupon;

  const validFor = useMemo(
    () => (
      <CouponApplicableBy
        couponId={couponId}
        ruleType={ruleType}
        tenantId={tenantId}
        tenantName={tenantName}
        rules={couponRules}
      />
    ),
    [couponId, couponRules, ruleType, tenantId, tenantName],
  );
  const automaticCodeCreation = idType === redemptionCodeEnums.auto;
  const couponDescriptions = [
    {
      label: 'Valid for',
      formatter: () => validFor,
    },
    {
      label: 'Number of Redemptions',
      formatter: () => {
        if (redemptionType === numberOfRedemptionEnums.unlimited) {
          return numberOfRedemptionEnums.unlimited;
        }
        return `${timesRedeemed}/${maxRedemption}`;
      },
    },
    {
      label: automaticCodeCreation ? 'Code Creation' : 'Redemption Code',
      formatter: () => (automaticCodeCreation ? 'Automatic' : couponId),
    },
    {
      label: 'Discount',
      formatter: () => (discountType === discountTypeEnums.percentOff ? `${percentOff}%` : `$${priceToString(amountOff)}`),
    },
    {
      label: 'Applies To',
      formatter: () => (duration === discountDurationEnums.once ? 'The first bill after redemption' : `Each bill for ${durationInMonths} months`),
    },
    {
      label: 'Expiration',
      formatter: () => (redeemBy?.isValid() ? redeemBy.format('MM/DD/YYYY') : 'None'),
    },
    {
      label: 'Description',
      key: 'description',
      readOnly: true,
      editor: TextAreaEditor,
    },
  ];
  return (
    <Card sx={{ maxWidth: 900 }}>
      <CardHeader
        sx={{ background: 'RGB(0,0,0 , .03)', borderRadius: '5px 5px 0 0' }}
        title={couponIsCreated ? 'Internet Service Coupon' : 'New Coupon Preview'}
      />
      <Divider />
      <CardContent>
        <List>
          <DataFormatter
            data={{ description }}
            descriptions={couponDescriptions}
            saveChanges={saveChanges}
            updateValue={updateValue}
            updatedData={updatedData}
          />
        </List>
      </CardContent>
      <CardActions disableSpacing sx={{ background: 'RGB(0,0,0 , .03)', justifyContent: 'right' }}>
        {children}
      </CardActions>
    </Card>
  );
}
